<script lang="ts" setup>
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import FadeSlideTransition from '@shared/animation/transitions/FadeSlideTransition/FadeSlideTransition.vue';
import FloatLabel from '@shared/form-v2/FloatLabel/FloatLabel.vue';
import { useBlurValidatedField } from '@shared/validation/useBlurValidatedField';
import { toTypedSchema } from '@vee-validate/zod';
import { watchDebounced } from '@vueuse/core';
import { useFieldValue } from 'vee-validate';
import { computed, reactive, useId, watch } from 'vue';
import NumberField from '../NumberField/NumberField.vue';
import PostCodeField from '../PostCodeField/PostCodeField.vue';
import StringField from '../StringField/StringField.vue';
import VSelect from '../VSelect/VSelect.vue';
import { type AdresFieldPaths, type AdresLookup } from './types';
import { validations } from './validation';

export interface AdresFieldProps {
  adresFieldPaths: AdresFieldPaths;
  autocomplete?: boolean;
  disabled?: boolean;
  lookup: AdresLookup;
  readonly?: boolean;
}

const props = withDefaults(defineProps<AdresFieldProps>(), {
  autocomplete: true,
});

const state = reactive<{
  error?: string;
  toevoegingen: string[];
}>({
  toevoegingen: [],
});

const huisnummerField = useBlurValidatedField(
  props.adresFieldPaths.huisnummer,
  toTypedSchema(validations.huisnummer),
);
const toevoegingInitialValue = useFieldValue<string>(props.adresFieldPaths.huisnummertoevoeging);
const toevoegingField = useBlurValidatedField<string | undefined>(
  props.adresFieldPaths.huisnummertoevoeging,
  toTypedSchema(validations.huisnummertoevoeging),
  { initialValue: toevoegingInitialValue.value || undefined },
);
const plaatsField = useBlurValidatedField(
  props.adresFieldPaths.plaats,
  toTypedSchema(validations.plaats),
);
const postcodeField = useBlurValidatedField(
  props.adresFieldPaths.postcode,
  toTypedSchema(validations.postcode),
);
const straatField = useBlurValidatedField(
  props.adresFieldPaths.straat,
  toTypedSchema(validations.straat),
);
const id = useId();

const hasToevoegingen = computed(
  () =>
    state.toevoegingen.length && !(state.toevoegingen.length === 1 && state.toevoegingen[0] === ''),
);

const isFilled = computed(
  () =>
    straatField.value.value ||
    huisnummerField.value.value ||
    toevoegingField.value.value ||
    postcodeField.value.value ||
    plaatsField.value.value,
);

async function getData(postcode?: string | null, huisnummer?: number | null) {
  if (postcode?.length && huisnummer) {
    state.error = undefined;
    if (postcode.length < 7 || !huisnummer) return;
    const res = await props.lookup(postcode, huisnummer);

    if (!res.adres || !res.success || res.error) {
      state.error = res.error;
      resetExtraFields();
      return;
    }

    straatField.setValue(res.adres.straat ?? '');
    plaatsField.setValue(res.adres.plaats ?? '');
    state.toevoegingen = res.adres.toevoegingen;
  } else {
    resetExtraFields();
  }
}

function initHuisnummertoevoegingValue(toevoegingen: string[]) {
  const hasBlankToevoeging = toevoegingen.includes('');

  if (
    toevoegingField.value.value !== undefined &&
    !toevoegingen.includes(toevoegingField.value.value)
  ) {
    toevoegingField.setValue(hasBlankToevoeging ? '' : undefined);
  } else if (toevoegingField.value.value === undefined && hasBlankToevoeging) {
    toevoegingField.setValue('');
  } else if (toevoegingen.length === 1) {
    toevoegingField.setValue(toevoegingen[0]);
  }
}

function resetExtraFields() {
  if (straatField.value.value) {
    straatField.setValue('');
  }

  if (plaatsField.value.value) {
    plaatsField.setValue('');
  }

  state.toevoegingen = [];
}

watch(() => state.toevoegingen, initHuisnummertoevoegingValue);
watchDebounced(
  [postcodeField.value, huisnummerField.value],
  ([postcode, huisnummer]) => getData(postcode, huisnummer),
  { debounce: 500, immediate: true },
);
</script>

<template>
  <div
    v-if="!props.readonly"
    class="address-wrapper flex basis-full flex-wrap gap-x-2 gap-y-4"
    data-testid="adres-field"
  >
    <FloatLabel class="grow-0 basis-28 *:grow">
      <PostCodeField
        v-model="postcodeField.value.value"
        data-testid="adres-postcode"
        :disabled="props.disabled"
        :has-error="!!state.error || !!postcodeField.errorMessage.value"
        :id="`adres-postcode-${id}`"
        path="postcode"
        @blur="postcodeField.handleBlur()"
      />

      <label :for="`adres-postcode-${id}`">Postcode</label>
    </FloatLabel>

    <FloatLabel class="grow-0 basis-36">
      <NumberField
        v-model:value="huisnummerField.value.value"
        data-testid="adres-huisnummer"
        :disabled="props.disabled"
        :has-error="!!state.error || !!huisnummerField.errorMessage.value"
        :id="`adres-huisnummer-${id}`"
        path="huisnummer"
        @blur="huisnummerField.handleBlur()"
        placeholder="Huisnr."
      />

      <label :for="`adres-huisnummer-${id}`">Huisnummer</label>
    </FloatLabel>

    <FloatLabel
      :class="[
        '-me-2 -ms-2 grow-0 overflow-x-clip transition-[flex-basis,_flex-grow,_padding-inline] duration-400',
        {
          'basis-0': !hasToevoegingen,
          'basis-40 pe-2 ps-2': hasToevoegingen,
        },
      ]"
      :hidden="!hasToevoegingen"
    >
      <VSelect
        v-model="toevoegingField.value.value"
        data-testid="adres-huisnummer-toevoeging"
        :disabled="props.disabled || !hasToevoegingen"
        :id="`adres-huisnummertoevoeging-${id}`"
        :invalid="!!state.error || !!toevoegingField.errorMessage.value"
        :options="state.toevoegingen"
        searchable
      />

      <label :for="`adres-huisnummertoevoeging-${id}`">Toevoeging</label>
    </FloatLabel>

    <FadeSlideTransition class="basis-full" direction="down">
      <FloatLabel v-if="straatField.value.value">
        <StringField
          v-model="straatField.value.value"
          data-testid="adres-straat"
          disabled
          :has-error="!!state.error || !!straatField.errorMessage.value"
          :id="`adres-straat-${id}`"
          @blur="straatField.handleBlur()"
        />

        <label :for="`adres-straat-${id}`">Straat</label>
      </FloatLabel>
    </FadeSlideTransition>

    <FadeSlideTransition class="basis-full" direction="down">
      <FloatLabel v-if="plaatsField.value.value">
        <StringField
          v-model="plaatsField.value.value"
          data-testid="adres-plaats"
          disabled
          :has-error="!!state.error || !!plaatsField.errorMessage.value"
          :id="`adres-plaats-${id}`"
          @blur="plaatsField.handleBlur()"
        />

        <label :for="`adres-plaats-${id}`">Plaats</label>
      </FloatLabel>
    </FadeSlideTransition>

    <ExpandTransition>
      <span v-if="!!state.error" class="text-sm text-red" role="status">
        {{ state.error }}
      </span>
    </ExpandTransition>
  </div>

  <div v-else>
    {{
      isFilled
        ? `${straatField.value.value || ''} ${huisnummerField.value.value || ''}${
            toevoegingField.value.value ? `-${toevoegingField.value.value}` : ''
          }, ${postcodeField.value.value || ''}, ${plaatsField.value.value || ''}`
        : '-'
    }}
  </div>
</template>
